import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { AppShell } from "../layouts/AppShell.jsx";
import { StatePanel } from "../components/primitives.jsx";
import ReportPage from "../pages/ReportPage.jsx";
import ContentPage from "../pages/ContentPage.jsx";
import UserPage from "../pages/UserPage.jsx";
import LoginPage from "../pages/LoginPage.jsx";
import { AuthProvider, useAuth } from "../auth/AuthProvider.jsx";
import { TenantProvider, tabEnabled, useTenant } from "../tenant/TenantProvider.jsx";
import { DASHBOARDS, findDashboard, findTab } from "../../shared/catalog.js";
import { P, TYPE_PERMISSION } from "../../shared/permissions.js";

/** First tab the user may open in the current tenant, as a path; null when none. */
// eslint-disable-next-line react-refresh/only-export-components
export function firstAllowedPath(can, current) {
  for (const d of DASHBOARDS) {
    const t = d.tabs.find((x) => tabEnabled(x, current) && can(x.permission));
    if (t) return `/${d.id}/${t.id}`;
  }
  return null;
}

function NoAccess({ what }) {
  return <StatePanel title="No access" detail={`You do not have permission to view ${what} for this tenant. Ask an administrator if you need it.`} />;
}

function Home() {
  const { search } = useLocation();
  const { can } = useAuth();
  const { current } = useTenant();
  const to = firstAllowedPath(can, current);
  return to ? <Navigate replace to={{ pathname: to, search }} /> : <AppShell><NoAccess what="any analytics" /></AppShell>;
}

function DashboardRoute() {
  const { dashboard, tab } = useParams();
  const { search } = useLocation();
  const { current } = useTenant();
  const { can } = useAuth();
  const dash = findDashboard(dashboard);
  const meta = findTab(dashboard, tab);
  const allowed = (t) => tabEnabled(t, current) && can(t.permission);

  // Unknown, disabled or not-permitted tab → the dashboard's first allowed tab, else the first allowed anywhere.
  if (!dash || !meta || !allowed(meta)) {
    const first = dash?.tabs.find(allowed);
    const to = first ? `/${dash.id}/${first.id}` : firstAllowedPath(can, current);
    if (to && to !== `/${dashboard}/${tab}`) return <Navigate replace to={{ pathname: to, search }} />;
    return (
      <AppShell>
        <NoAccess what="this page" />
      </AppShell>
    );
  }

  return (
    <AppShell>
      <ReportPage key={`${dashboard}/${tab}`} />
    </AppShell>
  );
}

function ContentRoute() {
  const { type } = useParams();
  const { can } = useAuth();
  return <AppShell>{can(TYPE_PERMISSION[type]) ? <ContentPage /> : <NoAccess what="this content" />}</AppShell>;
}

function UserRoute() {
  const { can } = useAuth();
  return <AppShell>{can(P.users) ? <UserPage /> : <NoAccess what="user details" />}</AppShell>;
}

function SignedInApp() {
  return (
    <AuthProvider>
      <TenantProvider>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/user/:uid" element={<UserRoute />} />
          <Route path="/content/:type/:id" element={<ContentRoute />} />
          <Route path="/:dashboard" element={<DashboardRoute />} />
          <Route path="/:dashboard/:tab" element={<DashboardRoute />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </TenantProvider>
    </AuthProvider>
  );
}

export default function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/*" element={<SignedInApp />} />
    </Routes>
  );
}
